import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map ISO country code (2-letter) -> { flag emoji, fr name, en name }
const COUNTRY_MAP = {
  FR: { flag: '🇫🇷', fr: 'France',          en: 'France' },
  BE: { flag: '🇧🇪', fr: 'Belgique',        en: 'Belgium' },
  CH: { flag: '🇨🇭', fr: 'Suisse',          en: 'Switzerland' },
  LU: { flag: '🇱🇺', fr: 'Luxembourg',      en: 'Luxembourg' },
  DE: { flag: '🇩🇪', fr: 'Allemagne',       en: 'Germany' },
  AT: { flag: '🇦🇹', fr: 'Autriche',        en: 'Austria' },
  IT: { flag: '🇮🇹', fr: 'Italie',          en: 'Italy' },
  ES: { flag: '🇪🇸', fr: 'Espagne',         en: 'Spain' },
  PT: { flag: '🇵🇹', fr: 'Portugal',        en: 'Portugal' },
  NL: { flag: '🇳🇱', fr: 'Pays-Bas',        en: 'Netherlands' },
  PL: { flag: '🇵🇱', fr: 'Pologne',         en: 'Poland' },
  GB: { flag: '🇬🇧', fr: 'Royaume-Uni',     en: 'United Kingdom' },
  IE: { flag: '🇮🇪', fr: 'Irlande',         en: 'Ireland' },
  US: { flag: '🇺🇸', fr: 'États-Unis',      en: 'United States' },
  CA: { flag: '🇨🇦', fr: 'Canada',          en: 'Canada' },
  MA: { flag: '🇲🇦', fr: 'Maroc',           en: 'Morocco' },
  DZ: { flag: '🇩🇿', fr: 'Algérie',         en: 'Algeria' },
  TN: { flag: '🇹🇳', fr: 'Tunisie',         en: 'Tunisia' },
  JP: { flag: '🇯🇵', fr: 'Japon',           en: 'Japan' },
  AU: { flag: '🇦🇺', fr: 'Australie',       en: 'Australia' },
};

const CACHE_KEY = 'rm_detected_country_v1';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const DISMISS_KEY = 'rm_ship_banner_dismissed';

export const useDetectedCountry = () => {
  const [country, setCountry] = useState(null);

  useEffect(() => {
    // Try cache
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached.code && cached.ts && (Date.now() - cached.ts) < CACHE_TTL_MS) {
          setCountry(cached.code);
          return;
        }
      }
    } catch { /* ignore */ }

    // Fallback to browser locale
    const localeCode = (() => {
      const lang = (navigator.languages && navigator.languages[0]) || navigator.language || '';
      const parts = lang.split(/[-_]/);
      const match = (parts[1] || '').toUpperCase().match(/^[A-Z]{2}/);
      return match ? match[0] : null;
    })();

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/geo/country`);
        if (!res.ok) throw new Error('geoip failed');
        const data = await res.json();
        const code = (data.country || '').toUpperCase();
        if (code && !cancelled) {
          setCountry(code);
          localStorage.setItem(CACHE_KEY, JSON.stringify({ code, ts: Date.now() }));
          return;
        }
      } catch { /* fall through to locale-based fallback */ }
      if (!cancelled && localeCode) {
        setCountry(localeCode);
        localStorage.setItem(CACHE_KEY, JSON.stringify({ code: localeCode, ts: Date.now() }));
      }
    })();

    return () => { cancelled = true; };
  }, []);

  return country;
};

export const ShipToCountryBanner = ({ language = 'fr' }) => {
  const country = useDetectedCountry();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  if (!country || dismissed) return null;
  const info = COUNTRY_MAP[country];
  if (!info) return null;

  const name = language === 'fr' ? info.fr : info.en;
  const text = language === 'fr'
    ? `Nous expédions vers ${info.flag} ${name}`
    : `We ship to ${info.flag} ${name}`;

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
  };

  return (
    <div className="hidden sm:flex items-center gap-2 text-xs text-white/90" data-testid="ship-to-country-banner">
      <span className="opacity-80">{text}</span>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="dismiss"
        className="opacity-60 hover:opacity-100 transition-opacity"
        data-testid="dismiss-ship-banner"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};
